import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { fontSize, variant } from "styled-system";
import PropTypes from "prop-types";
import { Box, Flex, Text, Loader } from "atoms";

const ButtonBase = styled(Box)`
  position: relative;
  cursor: pointer;
  outline: none;
  display: grid;
  align-items: center;

  ${variant({
    variants: {
      primary: {
        color: "white",
        bg: "primary.900",
        fontFamily: "NeometricBold",
        fontSize: "1.6rem",
        borderRadius: "1rem",
        border: "1px solid",
        borderColor: "primary.900",
        cursor: "pointer",
        transition: "all .3s",
        "&:active, &:hover": {
          bg: "white",
          color: "primary.900",
          border: "1px solid",
          borderColor: "primary.900",
        },
        "&:focus": {
          outline: "none",
          bg: "white",
          color: "primary.900",
          border: "1px solid",
          borderColor: "primary.900",
        },
      },
      primaryOutline: {
        color: "primary.900",
        bg: "transparent",
        fontFamily: "NeometricBold",
        fontSize: "1.6rem",
        borderRadius: "1rem",
        border: "1px solid",
        borderColor: "primary.900",
        cursor: "pointer",
        transition: "all .3s",
        "&:active, &:hover": {
          bg: "primary.900",
          color: "white",
          border: "1px solid",
          borderColor: "primary.900",
        },
        "&:focus": {
          outline: "none",
          bg: "primary.900",
          color: "white",
          border: "1px solid",
          borderColor: "primary.900",
        },
      },
      outline: {
        background: "white",
        color: "secondary.700",
        fontSize: "1.6rem",
        borderRadius: "8px",
        border: "2px solid rgba(255, 136, 0, 1)",
        cursor: "pointer",
        transition: "all .3s",
        fontWeight: 600,
        "&:active, &:hover": {
          bg: "secondary.900",
          color: "neutral.50",
        },
        "&:focus": {
          bg: "secondary.700",
          color: "neutral.50",
        },
      },
      secondary: {
        color: "white",
        bg: "secondary.500",
        fontFamily: "primaryBold",
        fontSize: "1.6rem",
        border: "1px solid",
        borderColor: "secondary.500",
        borderRadius: "1rem",
        cursor: "pointer",
        transition: "all .3s",
        // width: "100%",
        paddingLeft: "2rem",
        paddingRight: "2rem",
        "&:active, &:hover": {
          color: "secondary.500",
          bg: "transparent",
          fontFamily: "primaryBold",
        },
        "&:focus": {
          bg: "secondary.500",
          color: "white",
          fontFamily: "primaryBold",
          outline: "none",
        },
      },
      error: {
        bg: "white",
        color: "error.900",
        fontSize: "1.6rem",
        borderRadius: "8px",
        border: "2px solid rgba(255, 8, 0, 1)",
        cursor: "pointer",
        transition: "all .3s",
        fontWeight: 600,
        "&:active, &:hover": {
          bg: "error.50",
          color: "error.900",
        },
        "&:focus": {
          bg: "error.900",
          color: "neutral.50",
        },
      },
      dash: {
        bg: "white",
        color: "success.900",
        fontSize: "1.6rem",
        borderRadius: "8px",
        border: "2px dashed rgba(0, 108, 17, 1)",
        cursor: "pointer",
        transition: "all .3s",
        fontWeight: 600,
        "&:active, &:hover": {
          bg: "success.900",
          color: "neutral.50",
        },
        "&:focus": {
          bg: "success.700",
          color: "neutral.50",
        },
      },
      disabled: {
        color: "white",
        bg: "neutral.300",
        cursor: "not-allowed",
        borderRadius: "8px",
        border: "none",
      },
    },
  })}
  &[disabled] {
    color: "neutral.400";
    background-color: "neutral.200";
    cursor: not-allowed;
  }
`;

export const Button = ({
  disabled,
  loading,
  IconProps,
  children,
  variant: v = "primary",
  onClick,
  showAsyncLoad,
  fontWeight,
  fontFamily,
  classes,
  label,
  height,
  ...rest
}) => {
  const { Icon, IconCss, IconBoxCss } = IconProps || {};

  const [asyncLoading, setLoading] = useState(false);
  const onClickHandler = useCallback(
    async (...arg) => {
      setLoading(true);
      try {
        await onClick?.(arg);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    },
    [onClick]
  );

  return (
    <ButtonBase
      variant={disabled ? "disabled" : v}
      as="button"
      color="white"
      onClick={onClickHandler}
      disabled={disabled || loading || (showAsyncLoad && asyncLoading)}
      overflow="hidden"
      className={classes}
      height={height}
      {...rest}
    >
      {(loading || (showAsyncLoad && asyncLoading)) && (
        <Flex
          left={0}
          right={0}
          position="absolute"
          justifyContent="center"
          alignItems="center"
          fontSize={2}
          height="15px"
          width="15px"
          mx="auto"
        >
          <Loader loading />
        </Flex>
      )}
      <Flex aI="center" jC="center">
        {Icon && (
          <Box mr=".8rem" width="1.5rem" {...IconBoxCss}>
            <Icon {...IconCss} />
          </Box>
        )}
        {label ? (
          <Text
            color="inherit"
            opacity={loading || (showAsyncLoad && asyncLoading) ? 0 : 1}
            fontSize={fontSize}
            fontWeight={fontWeight}
          >
            {label}
          </Text>
        ) : (
          <Text
            color="inherit"
            opacity={loading || (showAsyncLoad && asyncLoading) ? 0 : 1}
            fontSize={fontSize}
            fontWeight={fontWeight}
            fontFamily={fontFamily}
          >
            {children}
          </Text>
        )}
      </Flex>
    </ButtonBase>
  );
};

Button.defaultProps = {
  as: "button",
  width: "100%",
  fontSize: "1.6rem",
  fontFamily: "NeometricBold",
};

Button.propTypes = {
  fontSize: PropTypes.string,
  as: PropTypes.string,
  width: PropTypes.string,
  fontFamily: PropTypes.string,
};
